import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader, Minus, Maximize2, Minimize2 } from 'lucide-react';
import './EmailInbox.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ComposeModal({ account, initialData, onClose, onSent }) {
  const token = localStorage.getItem('crm_token');
  const [to, setTo]         = useState(initialData?.to?.join(', ') || '');
  const [cc, setCc]         = useState('');
  const [bcc, setBcc]       = useState('');
  const [subject, setSubject] = useState(initialData?.subject || '');
  const [body, setBody]     = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [minimized, setMin] = useState(false);
  const [maximized, setMax] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError]   = useState('');
  const editorRef           = useRef(null);

  // Insert reply quote
  useEffect(() => {
    if (initialData?.quoteHtml && editorRef.current) {
      editorRef.current.innerHTML = `<br/><br/><div style="border-left:3px solid #6c63ff;padding:8px 0 8px 16px;color:#888899;margin:8px 0">${initialData.quoteHtml}</div>`;
    }
  }, [initialData]);

  const handleSend = async () => {
    if (!to.trim()) { setError('Please add at least one recipient.'); return; }
    if (!account) { setError('No email account selected.'); return; }
    setSending(true);
    setError('');
    try {
      const bodyHtml = editorRef.current?.innerHTML || body;
      const toArr  = to.split(',').map(e => e.trim()).filter(Boolean);
      const ccArr  = cc.split(',').map(e => e.trim()).filter(Boolean);
      const bccArr = bcc.split(',').map(e => e.trim()).filter(Boolean);

      const res = await fetch(`${API}/email-inbox/accounts/${account._id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: toArr,
          cc: ccArr.length ? ccArr : undefined,
          bcc: bccArr.length ? bccArr : undefined,
          subject,
          bodyHtml,
          inReplyTo: initialData?.inReplyTo || undefined,
          references: initialData?.references || undefined
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to send');
      onSent();
    } catch (e) {
      setError(e.message);
      setSending(false);
    }
  };

  const modalStyle = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: maximized ? '90vw' : 620,
    height: maximized ? '90vh' : minimized ? 52 : 'auto',
    maxWidth: '95vw',
    background: '#1c1c30',
    border: '1px solid rgba(108,99,255,0.25)',
    borderRadius: 16,
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'all 0.2s ease'
  };

  return (
    <div style={modalStyle}>
      {/* Header */}
      <div className="ei-compose-header">
        <h3>{initialData?.subject ? `Re: ${initialData.subject}` : 'New Message'}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="ei-icon-btn" style={{ width: 28, height: 28 }} onClick={() => setMin(!minimized)} title={minimized ? 'Expand' : 'Minimize'}>
            <Minus size={14} />
          </button>
          <button className="ei-icon-btn" style={{ width: 28, height: 28 }} onClick={() => setMax(!maximized)} title={maximized ? 'Restore' : 'Maximize'}>
            {maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button className="ei-icon-btn" style={{ width: 28, height: 28 }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          <div className="ei-compose-body">
            {/* Error */}
            {error && (
              <div style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: 13, borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
                {error}
              </div>
            )}

            {/* To */}
            <div className="ei-compose-field">
              <span className="ei-compose-field-label">To</span>
              <input
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="Recipients (comma-separated)"
              />
              <button onClick={() => setShowCc(!showCc)} style={{ background: 'none', border: 'none', color: '#6c63ff', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>Cc</button>
              <button onClick={() => setShowBcc(!showBcc)} style={{ background: 'none', border: 'none', color: '#6c63ff', fontSize: 12, cursor: 'pointer', flexShrink: 0, marginLeft: 6 }}>Bcc</button>
            </div>

            {showCc && (
              <div className="ei-compose-field">
                <span className="ei-compose-field-label">Cc</span>
                <input value={cc} onChange={e => setCc(e.target.value)} placeholder="Carbon copy" />
              </div>
            )}

            {showBcc && (
              <div className="ei-compose-field">
                <span className="ei-compose-field-label">Bcc</span>
                <input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="Blind carbon copy" />
              </div>
            )}

            {/* Subject */}
            <div className="ei-compose-field">
              <span className="ei-compose-field-label">Sub</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" />
            </div>

            {/* Body */}
            <div
              ref={editorRef}
              className="ei-compose-editor"
              contentEditable
              suppressContentEditableWarning
              data-placeholder="Write your message…"
              style={{ minHeight: maximized ? '60vh' : 240 }}
              onInput={e => setBody(e.currentTarget.innerHTML)}
            />
          </div>

          {/* Toolbar */}
          <div className="ei-compose-toolbar">
            <button className="ei-btn-primary" onClick={handleSend} disabled={sending}>
              {sending ? <Loader size={16} className="ei-spinner" /> : <Send size={16} />}
              {sending ? 'Sending…' : 'Send'}
            </button>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#444455' }}>
              From: {account?.emailAddress || 'No account selected'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
